<template>
  <div class="flex h-full p-1">
    <div v-if="currentView === 'actions'" class="flex flex-1 flex-col gap-3">
      <button
        type="button"
        data-tint="primary"
        class="relative block w-full overflow-hidden rounded-md bg-secondary-300 p-2 text-left font-semibold capitalize-first"
        @click="currentView = 'form'"
      >
        {{ $t('button.change_layout_options') }}
        <FontAwesomeIcon
          class="absolute -right-0.5 -bottom-0.5 opacity-25"
          size="2x"
          icon="cogs"
        />
      </button>
      <button
        v-if="headers.length"
        type="button"
        data-tint="primary"
        class="relative block w-full overflow-hidden rounded-md bg-secondary-300 p-2 text-left font-semibold capitalize-first"
        @click="currentView = 'header'"
      >
        {{ $t('button.change_the_letterhead') }}
        <FontAwesomeIcon
          class="absolute -right-0.5 -bottom-0.5 opacity-25"
          size="2x"
          :icon="['fal', 'file-invoice']"
        />
      </button>
      <div
        class="grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(10rem,_1fr))] gap-3"
      >
        <button
          v-for="action in actions"
          :key="action.event"
          type="button"
          data-tint="primary"
          class="relative overflow-hidden rounded-md bg-primary-300 p-2 text-left font-semibold capitalize-first"
          @click="$emit(action.event)"
        >
          <div class="truncate">{{ $t(action.label) }}</div>
          <FontAwesomeIcon
            class="absolute -right-0.5 -bottom-0.5 opacity-25"
            size="2x"
            :icon="action.icon"
          />
        </button>
      </div>
    </div>

    <div v-else class="flex flex-1 flex-col gap-4">
      <div class="flex min-h-0 flex-1">
        <div class="scrollbar-custom flex flex-1 flex-col gap-6 overflow-auto">
          <template v-if="currentView === 'form'">
            <div class="flex flex-row items-center gap-x-2">
              <span class="shrink-0">
                <BaseButton
                  icon="chevron-left"
                  size="extra-small"
                  @click="currentView = 'actions'"
                />
              </span>
              <h2 class="flex-1 text-xl font-bold capitalize-first">
                {{ $t('attributes.commission_note') }}
              </h2>
            </div>
            <div class="flex flex-col gap-x-2">
              <h3 class="text-lg font-bold text-gray-700 capitalize-first">
                {{ $t('global.options') }}
              </h3>
              <CommissionNotePrintOptions
                :model-value="options"
                :footers="footers"
              />
            </div>
          </template>
          <HeaderSelector
            v-else
            :header="selectedHeader"
            :header-list="headers"
            current-view="header"
            @update:header="selectHeader"
            @update:current-view="currentView = $event"
          />
        </div>
      </div>
      <div class="flex shrink-0 flex-row justify-between">
        <BaseButton
          v-if="currentView === 'form' && headers.length"
          icon="file-invoice"
          :title="$t('attributes.header')"
          size="small"
          color="light"
          @click="currentView = 'header'"
        />
        <BaseButton
          v-if="currentView === 'header'"
          icon="list"
          size="small"
          @click="currentView = 'form'"
        />
        <BaseButton
          v-if="changed"
          theme-color="bg-secondary text-contrast-secondary"
          :title="$t('global.refresh_document')"
          icon="arrows-rotate"
          size="small"
          @click="refresh"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import HeaderSelector from '@c/Media/HeaderSelector.vue'
import CommissionNotePrintOptions from '@/views/invoicing/show/commissionNote/components/CommissionNotePrintOptions.vue'
import { NO_HEADER } from '@/views/invoicing/show/commissionNote/components/commissionNotePrint'

/**
 * The menu on the right of the print page, laid out like the one of the
 * invoices: layout options, letterhead and the actions (send, print, save).
 * The PDF is only rebuilt on `refresh`, as for the invoices.
 */
export default {
  name: 'CommissionNotePrintMenu',
  components: {
    BaseButton,
    CommissionNotePrintOptions,
    FontAwesomeIcon,
    HeaderSelector
  },
  props: {
    options: { type: Object, required: true },
    headers: { type: Array, default: () => [] },
    footers: { type: Array, default: null },
    canSend: { type: Boolean, default: false }
  },
  emits: ['refresh', 'send', 'print', 'save'],
  data() {
    return { currentView: 'actions', changed: false }
  },
  computed: {
    actions() {
      return [
        {
          event: 'send',
          label: 'button.send_by_email',
          icon: 'paper-plane',
          gate: this.canSend
        },
        { event: 'print', label: 'button.print', icon: 'print', gate: true },
        { event: 'save', label: 'button.save', icon: 'download', gate: true }
      ].filter((action) => action.gate)
    },
    // the blank paper of the selector is the pre-printed paper of the API;
    // without a choice the API uses the header of the agency settings, shown as the favorite one
    selectedHeader() {
      if (this.options.header === NO_HEADER) {
        return null
      }

      return (
        this.options.header ??
        this.headers.find((header) => header.favorite)?.id ??
        null
      )
    }
  },
  watch: {
    options: {
      handler() {
        this.changed = true
      },
      deep: true
    }
  },
  methods: {
    selectHeader(header) {
      this.options.header = header ?? NO_HEADER
      this.currentView = 'actions'
    },
    refresh() {
      this.changed = false
      this.$emit('refresh')
    }
  }
}
</script>
